<template>
  <div v-if="rows.length > 0" class="preview-table">
    <table class="preview-table__table">
      <thead>
        <tr>
          <th class="preview-table__status">
            <!-- The column shows only status icons; the label is for assistive tech. -->
            <span class="preview-table__sr-only">{{ columnLabels.status }}</span>
          </th>
          <th>{{ columnLabels.name }}</th>
          <th>{{ columnLabels.type }}</th>
          <th>{{ columnLabels.resultingSpecialties }}</th>
          <th v-if="clientControlEnabled">{{ columnLabels.control }}</th>
        </tr>
      </thead>
      <tbody>
        <!-- Term names are deduped server-side, so they are a stable row key. -->
        <tr
          v-for="row in rows"
          :key="row.name"
          :class="rowClass(row)"
          :data-testid="TestId.assignSpecialties.previewRow"
        >
          <td class="preview-table__status">
            <RowStatusIcon :status="iconStatus(row)" :label="statusLabel(row)" />
          </td>
          <td>{{ row.name }}</td>
          <td>{{ row.type }}</td>
          <td>
            <!-- The column header already names the attribute, so the diff renders
                 without per-attribute labels. -->
            <ChangeSetDiff :changeset="specialtyChangeset(row)" :labels="diffLabels" :show-attribute-labels="false" />
          </td>
          <td v-if="clientControlEnabled" :data-testid="TestId.assignSpecialties.flagOutcome">
            {{ outcomeText(row) }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { TestId } from "#/test-ids";
import ChangeSetDiff from "#ce/apps/SpAdmin/shared/components/ChangeSetDiff.ce.vue";
import RowStatusIcon from "#ce/apps/SpAdmin/shared/components/RowStatusIcon.ce.vue";

const props = defineProps({
  rows: {
    type: Array,
    default: () => [],
  },
  outcomeLabels: {
    type: Object,
    default: () => ({}),
  },
  statusLabels: {
    type: Object,
    default: () => ({}),
  },
  // The store's merged `labels` object; this component reads `columns` and
  // `invalidEntry` from it.
  labels: {
    type: Object,
    default: () => ({}),
  },
  clientControlEnabled: {
    type: Boolean,
    default: false,
  },
});

const columnLabels = computed(() => props.labels.columns ?? {});

const diffLabels = computed(() => ({ removedValue: props.labels.removedSpecialty }));

function specialtyChangeset(row) {
  return { specialties: [row.current_specialties ?? [], row.resulting_specialties ?? []] };
}

function iconStatus(row) {
  return ["new", "existing"].includes(row.status) ? row.status : "error";
}

function statusLabel(row) {
  if (row.status === "new") {
    return props.statusLabels.new;
  }

  if (row.status === "existing") {
    return props.statusLabels.existing;
  }

  return rowErrorText(row);
}

function rowClass(row) {
  if (row.status === "existing") {
    return "preview-table__row--existing";
  }

  if (row.status === "error") {
    return "preview-table__row--error";
  }

  return "";
}

function rowErrorText(row) {
  return row.errors?.join(", ") || props.labels.invalidEntry || "";
}

// New terms are created client-owned through the request workflow, so they show the
// new_term label regardless of outcome. Existing rows only call out the will_enable
// transition; every other outcome (already_enabled, not_needed, or absent when the
// control checkbox is off) reads as "Unchanged".
function outcomeText(row) {
  if (row.status === "new") {
    return props.outcomeLabels.new_term || "";
  }

  if (row.client_specialties_outcome === "will_enable") {
    return props.outcomeLabels.will_enable || "";
  }

  return props.outcomeLabels.unchanged || "";
}
</script>

<style scoped lang="scss">
.preview-table {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  border: var(--sp-ref-spacing-px) solid var(--sp-sys-color-outline-variant, #f9f9f9);
  border-radius: var(--sp-ref-spacing-2);

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--sp-sys-typescale-small-font-size, 0.875rem);

    th,
    td {
      padding: var(--sp-ref-spacing-4);
      text-align: left;
      border-bottom: var(--sp-ref-spacing-px) solid var(--sp-sys-color-outline-variant, #f9f9f9);
    }

    th {
      font-size: var(--sp-ref-font-size-small, 0.625rem);
      font-weight: var(--sp-ref-font-weight-normal, 400);
      letter-spacing: calc(var(--sp-ref-spacing-px) * 0.5);
      color: var(--sp-sys-color-on-surface-variant, #4a4a4a);
      position: sticky;
      top: 0;
    }

    tbody tr:last-child td {
      border-bottom: none;
    }

    tbody tr:nth-child(even) {
      background-color: var(--sp-sys-color-surface-variant, #f9f9f9);
    }
  }

  &__status {
    width: 2rem;
    text-align: center;
  }

  // Screen-reader-only text for the status column's assistive label.
  &__sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }
}
</style>
